// components/SalesInventoryExport.js
import React from "react";
import PropTypes from "prop-types";
import { Button } from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getAuthCredentials } from "../../utils/auth";

export default function SalesInventoryExport({ dateRange }) {
  const { token, posId } = getAuthCredentials();

  const handleExport = async () => {
    // 1) Fetch the data
    const { data } = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/v1/api/sales/inventory-report`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          posId,
          startDate: dayjs(dateRange[0]).format("YYYY-MM-DDTHH:mm:ss"),
          endDate: dayjs(dateRange[1]).format("YYYY-MM-DDTHH:mm:ss"),
          page: 1,
          limit: 1e6,
        },
      }
    );
    const { menuSummary, inventorySummary } = data;

    // 2) Create a new workbook
    const wb = new ExcelJS.Workbook();
    wb.creator = "Your App";
    wb.created = new Date();

    // 3) Sheet 1: Menu Breakdown
    const ws1 = wb.addWorksheet("Menu Breakdown", {
      views: [{ state: "frozen", ySplit: 3 }],
      properties: { defaultColWidth: 15 },
    });

    // Title row
    const title = `Inventory Usage Report (${dayjs(dateRange[0]).format(
      "MMM D, YYYY"
    )} – ${dayjs(dateRange[1]).format("MMM D, YYYY")})`;
    ws1.mergeCells("A1:E1");
    ws1.getCell("A1").value = title;
    ws1.getCell("A1").font = { size: 14, bold: true };
    ws1.getCell("A1").alignment = { horizontal: "center" };

    // Blank and header
    ws1.addRow([]);
    const header1 = ["Menu Item", "Unit", "Ingredient", "Qty Used", "Cost (₱)"];
    ws1.addRow(header1);
    const headerRow1 = ws1.getRow(3);
    headerRow1.font = { bold: true };
    headerRow1.alignment = { horizontal: "center" };
    headerRow1.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFEFEFEF" },
      };
    });

    // Data rows
    menuSummary.forEach((menu) => {
      // menu name row
      const mr = ws1.addRow([menu.menu]);
      ws1.mergeCells(`A${mr.number}:E${mr.number}`);
      mr.getCell(1).font = { bold: true };
      mr.getCell(1).border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      // ingredient lines
      menu.breakdown.forEach((ing) => {
        const row = ws1.addRow([
          "",
          ing.unit,
          ing.inventory,
          ing.qty,
          ing.cost,
        ]);
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      // total row
      const tr = ws1.addRow(["Total", "", "", "", menu.cost]);
      tr.getCell(1).font = { italic: true };
      tr.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // blank line
      ws1.addRow([]);
    });

    // 4) Sheet 2: Overall Consumption
    const ws2 = wb.addWorksheet("Overall Consumption", {
      views: [{ state: "frozen", ySplit: 1 }],
      properties: { defaultColWidth: 15 },
    });

    const header2 = [
      "Inventory",
      "Unit",
      "Previous Qty",
      "Current Qty",
      "Δ % (Qty)",
      "Previous Cost (₱)",
      "Current Cost (₱)",
      "Δ % (Cost)",
    ];
    ws2.addRow(header2);
    const headerRow2 = ws2.getRow(1);
    headerRow2.font = { bold: true };
    headerRow2.alignment = { horizontal: "center" };
    headerRow2.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFEFEFEF" },
      };
    });

    // Data + formulas
    inventorySummary.forEach((inv) => {
      const row = ws2.addRow([
        inv.name,
        inv.unit,
        inv.previousQty,
        inv.currentQty,
        null, // Δ % (Qty)
        inv.previousCost,
        inv.currentCost,
        null, // Δ % (Cost)
      ]);

      row.getCell(3).numFmt = "#,##0";
      row.getCell(4).numFmt = "#,##0";
      row.getCell(6).numFmt = "#,##0.00";
      row.getCell(7).numFmt = "#,##0.00";

      const r = row.number;
      row.getCell(5).value = {
        formula: `IF(C${r}=0,"-",ROUND((D${r}-C${r})/C${r}*100,1)/100)`,
      };
      row.getCell(5).numFmt = "0.00%";

      row.getCell(8).value = {
        formula: `IF(F${r}=0,"-",ROUND((G${r}-F${r})/F${r}*100,1)/100)`,
      };
      row.getCell(8).numFmt = "0.00%";

      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // Totals row
    const lastDataRow = ws2.lastRow.number;
    const totalRow = ws2.addRow([
      "TOTAL",
      "",
      "",
      "",
      "", // percent placeholder
      { formula: `SUM(F2:F${lastDataRow})` },
      { formula: `SUM(G2:G${lastDataRow})` },
      null,
    ]);
    totalRow.font = { bold: true };
    const tr = totalRow.number;
    totalRow.getCell(8).value = {
      formula: `IF(F${tr}=0,"-",ROUND((G${tr}-F${tr})/F${tr}*100,1)/100)`,
    };
    totalRow.getCell(8).numFmt = "0.00%";
    totalRow.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "double" },
        right: { style: "thin" },
      };
    });

    // 5) Export
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `Inventory_Report_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`);
  };

  return (
    <Button variant="contained" color="primary" onClick={handleExport}>
      Export to Excel
    </Button>
  );
}

SalesInventoryExport.propTypes = {
  dateRange: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
};
